// Game flow for a local game: whose turn it is, the bot's think, resign and
// draw by agreement. No canvas, no DOM — the scene renders what this reports,
// and tests drive it with a stub bot.
import { GameState } from '../game/GameState.js';
import { toFen } from '../game/fen.js';
import { flagOutcome } from '../game/clock.js';
import { MatchClock, type ClockReading } from './MatchClock.js';
import { opposite, type Color, type Move } from '../game/types.js';
import type { Outcome } from '../game/outcome.js';
import type { Difficulty } from '../settings/types.js';
import type { Thinking } from '../ai/BotPlayer.js';
import type { LocalOpponent } from '../ui/types.js';
import type { Match, MatchMove } from './Match.js';

export type { MatchMove };

/** The bot never answers faster than this — an instant reply reads as a glitch. */
export const MIN_BOT_THINK_MS = 450;

export type Think = (fen: string, difficulty: Difficulty, budgetMs?: number) => Thinking;

export interface LocalMatchOptions {
  readonly opponent: LocalOpponent;
  /** The human's colour vs the bot; ignored for hot-seat. */
  readonly humanColor: Color;
  readonly difficulty: Difficulty;
  readonly think: Think;
  readonly onMove: (played: MatchMove) => void;
  /** Any other state change: bot started/stopped thinking, game concluded, restart. */
  readonly onChange: () => void;
  readonly minThinkMs?: number;
  readonly wait?: (ms: number) => Promise<void>;
  /** Each side's starting time, or null for an untimed game. */
  readonly clockMs?: number | null;
  readonly now?: () => number;
  /** The position to play from (an import); the standard start otherwise. */
  readonly start?: GameState;
}

const defaultWait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export class LocalMatch implements Match {
  private gameState: GameState;
  private thinking: Thinking | null = null;
  private generation = 0;
  private readonly matchClock: MatchClock | null;
  private paused = false;
  /** A bot reply that landed while paused; played on resume. */
  private heldBotMove: Move | null = null;

  constructor(private readonly opts: LocalMatchOptions) {
    this.gameState = opts.start ?? GameState.start();
    this.matchClock = opts.clockMs == null ? null : new MatchClock(opts.clockMs, opts.now ?? Date.now);
  }

  /** Each side's remaining time now; null when untimed. */
  clock(): ClockReading | null {
    return this.matchClock?.reading() ?? null;
  }

  /**
   * A modal covers the board (local games only): stop the clock and hold
   * any bot reply until play resumes, so nothing moves behind the dialog.
   */
  setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    this.paused = paused;
    if (!this.gameState.isOver) {
      if (paused) this.matchClock?.pause();
      else this.matchClock?.resume();
    }
    const held = this.heldBotMove;
    if (!paused && held !== null) {
      this.heldBotMove = null;
      this.apply(held, 'bot');
    }
  }

  /** Call every frame: ends the game when the running side's time is up. */
  checkFlag(): void {
    if (this.gameState.isOver) return;
    const flagged = this.matchClock?.flagged() ?? null;
    if (flagged === null) return;
    this.finish(flagOutcome(this.gameState.position.board, flagged));
  }

  get state(): GameState {
    return this.gameState;
  }

  get opponent(): LocalOpponent { return this.opts.opponent; }
  get humanColor(): Color { return this.opts.humanColor; }

  get botThinking(): boolean {
    return this.thinking !== null || this.heldBotMove !== null;
  }

  isBotTurn(): boolean {
    return this.opts.opponent === 'bot' && !this.gameState.isOver && this.gameState.turn !== this.opts.humanColor;
  }

  /** Whether a person at this screen may move right now. */
  canHumanMove(): boolean {
    return !this.gameState.isOver && !this.isBotTurn();
  }

  start(): void {
    this.maybeThink();
  }

  playHuman(move: Move): boolean {
    if (!this.canHumanMove()) return false;
    this.apply(move, 'human');
    return true;
  }

  /** The side to move resigns (hot-seat), or the human does (vs bot). */
  resign(): void {
    if (this.gameState.isOver) return;
    const loser = this.opts.opponent === 'bot' ? this.opts.humanColor : this.gameState.turn;
    this.finish({ winner: opposite(loser), termination: 'resign' });
  }

  agreeDraw(): void {
    if (this.gameState.isOver) return;
    this.finish({ winner: null, termination: 'agreement' });
  }

  restart(): void {
    this.cancelThinking();
    this.gameState = this.opts.start ?? GameState.start();
    this.matchClock?.reset();
    this.opts.onChange();
    this.maybeThink();
  }

  dispose(): void {
    this.cancelThinking();
  }

  private finish(outcome: Outcome): void {
    this.cancelThinking();
    this.matchClock?.pause();
    this.gameState = this.gameState.conclude(outcome);
    this.opts.onChange();
  }

  private apply(move: Move, by: 'human' | 'bot'): void {
    const before = this.gameState.position;
    this.gameState = this.gameState.play(move);
    this.matchClock?.moved();
    if (this.gameState.isOver) this.matchClock?.pause();
    this.opts.onMove({ move: this.gameState.lastMove ?? move, before, by });
    this.maybeThink();
  }

  private maybeThink(): void {
    if (!this.isBotTurn() || this.thinking !== null) return;
    const generation = ++this.generation;
    const thinking = this.opts.think(toFen(this.gameState.position), this.opts.difficulty, this.botBudget());
    this.thinking = thinking;
    this.opts.onChange();
    const wait = this.opts.wait ?? defaultWait;
    void Promise.all([thinking.promise, wait(this.opts.minThinkMs ?? MIN_BOT_THINK_MS)])
      .then(([uci]) => {
        if (generation !== this.generation) return;
        this.thinking = null;
        const next = uci === null ? null : this.gameState.playUci(uci);
        const move = next?.lastMove;
        if (move === undefined) {
          this.opts.onChange();
          return;
        }
        if (this.paused) {
          this.heldBotMove = move;
          return;
        }
        this.apply(move, 'bot');
      })
      .catch(() => {
        if (generation !== this.generation) return;
        this.thinking = null;
        this.opts.onChange();
      });
  }

  private botBudget(): number | undefined {
    return this.matchClock?.budgetFor(this.gameState.turn);
  }

  private cancelThinking(): void {
    this.generation++;
    this.heldBotMove = null;
    this.thinking?.cancel();
    this.thinking = null;
  }
}
